import { Empty, Spin } from 'antd';
import { ReactElement, useEffect, useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';
import { SubCategoryStatisticDisplayMode } from '../../../../../../store/subCategory';
import { useTipStatistic } from './useTipStatistic';
import { queryFilterParams } from '../../../../../../utils';

interface CategoryChartProps {
  displayMode: SubCategoryStatisticDisplayMode;
  subCategoryId: string | undefined;
  light?: boolean;
  setAverageData?: (data: any) => void;
}

/** I'm just minimally refactoring code, it was written way worse by prev author :) */
interface CustomizedXAxisTickProps {
  x: number;
  y: number;
  payload: any;
  isLight?: boolean;
}

function CustomizedXAxisTick({
  x,
  y,
  payload,
  isLight,
  tipType,
}: CustomizedXAxisTickProps & {
  tipType: '%' | '$';
}): ReactElement<SVGElement> {
  const prefix = tipType === '$' ? '$' : tipType === '%' ? '' : '';
  const suffix = tipType === '%' ? '%' : '';

  const rawText = `${prefix}${parseFloat(payload.value)}${suffix}`;
  const maxLength = 10;
  const displayText =
    rawText.length > maxLength ? `${rawText.slice(0, maxLength)}…` : rawText;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={isLight ? 'rgba(0, 0, 0, 0.80)' : 'rgba(255, 255, 255, 0.80)'}
        fontWeight={700}
        fontSize={12}
      >
        {displayText}
      </text>
    </g>
  );
}

function CategoryChart({
  displayMode,
  subCategoryId,
  light,
  setAverageData,
}: CategoryChartProps) {
  const { getTipStatistic, response, loading } = useTipStatistic();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (subCategoryId) {
      getTipStatistic(subCategoryId);
    }
  }, [subCategoryId]);

  useEffect(() => {
    if (searchParams.get('refetchTips') && subCategoryId) {
      getTipStatistic(subCategoryId);
    }
  }, [searchParams.get('refetchTips'), subCategoryId]);

  const barChartData: {
    label: string;
    worker: number;
    customer: number;
  }[] = useMemo(() => {
    const graph = response?.data?.forGraph;

    if (!graph) return [];

    const customer =
      displayMode === 'percent'
        ? graph.customerPercents ?? []
        : graph.customerValues ?? [];
    const worker =
      displayMode === 'percent'
        ? graph.workerPercents ?? []
        : graph.workerValues ?? [];

    const map = new Map<
      string,
      { label: string; customer: number; worker: number }
    >();

    for (const item of customer) {
      const { label, value } = item;

      if (!map.has(label)) {
        map.set(label, { label, customer: 0, worker: 0 });
      }
      map.get(label)!.customer = value;
    }

    for (const item of worker) {
      const { label, value } = item;

      if (!map.has(label)) {
        map.set(label, { label, customer: 0, worker: 0 });
      }
      map.get(label)!.worker = value;
    }

    return [...map.values()].sort((a, b) => {
      const aNum = Number.parseInt(a.label, 10);
      const bNum = Number.parseInt(b.label, 10);

      return aNum - bNum;
    });
  }, [response, displayMode]);

  useEffect(() => {
    setAverageData?.(response?.data || null);
  }, [response, setAverageData]);

  return (
    <Spin spinning={loading}>
      <ResponsiveContainer height={300} width="100%">
        {barChartData.length > 0 &&
        barChartData.some((data) => data.customer || data.worker) ? (
          <BarChart data={barChartData}>
            <defs>
              <linearGradient
                id="actualGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="rgba(235, 239, 243, 0.90)" />
                <stop offset="100%" stopColor="rgba(182, 194, 205, 0.90)" />
              </linearGradient>
              <linearGradient
                id="expectedGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="rgba(111, 239, 146, 0.90)" />
                <stop offset="100%" stopColor="rgba(50, 178, 56, 0.90)" />
              </linearGradient>
            </defs>
            <CartesianGrid
              horizontal
              vertical={false}
              stroke="rgba(255, 255, 255, 0.30)"
            />
            <XAxis
              dataKey="label"
              interval={0}
              padding={{ left: 20, right: 20 }}
              tick={(props) =>
                CustomizedXAxisTick({
                  ...props,
                  isLight: false,
                  tipType: displayMode === 'percent' ? '%' : '$',
                })
              }
            />
            <YAxis
              interval={0}
              padding={{ top: 20 }}
              tick={{
                fill: light
                  ? 'color: rgba(0, 0, 0, 0.80)'
                  : 'rgba(255, 255, 255, 0.80)',
                fontWeight: 700,
              }}
              tickFormatter={(value) =>
                value >= 1000 ? `${Math.round(value / 1000)}k` : value
              }
            />
            <Bar
              dataKey="customer"
              fill="url(#expectedGradient)"
              radius={[8, 8, 0, 0]}
              name="Customer"
            />
            <Bar
              dataKey="worker"
              fill="url(#actualGradient)"
              radius={[8, 8, 0, 0]}
              name="Worker"
            />
          </BarChart>
        ) : (
          <Empty
            description={
              <span
                style={{
                  color: light ? 'black' : 'white',
                }}
              >
                Not enough data to display statistics
              </span>
            }
            style={{
              width: '100%',
              height: '300px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          />
        )}
      </ResponsiveContainer>
    </Spin>
  );
}

export default CategoryChart;
